export const semanticTypes = [
  "email",
  "phone",
  "person_name",
  "address_line",
  "company_name",
  "external_id",
  "free_text",
] as const;
export type SemanticType = (typeof semanticTypes)[number];
const semanticTypeNames: ReadonlySet<string> = new Set(semanticTypes);
export type PrivacyPolicy = "nonPii" | "null" | "redact" | SemanticType;
export type PrivacyValueType =
  | "string"
  | "number"
  | "boolean"
  | "null"
  | "unknown";
export type PrivacyNode =
  | { kind: "leaf"; policy: PrivacyPolicy; valueType: PrivacyValueType }
  | { kind: "object"; policy?: "null"; fields: Record<string, PrivacyNode> }
  | { kind: "array"; policy?: "null"; element: PrivacyNode }
  | {
      kind: "tuple";
      policy?: "null";
      elements: PrivacyNode[];
      rest?: PrivacyNode;
    }
  | {
      kind: "union";
      policy?: "null";
      variants: PrivacyNode[];
      discriminator?: string;
      variantValues?: string[];
    };

export interface JsonPrivacySchema {
  readonly _zod: object;
}

export type PrivacyAnnotation =
  | PrivacyPolicy
  | { kind: "structured" }
  | { kind: "jsonShape"; schema: JsonPrivacySchema };

const objectAnnotations = new WeakMap<object, PrivacyAnnotation>();
const objectConflicts = new WeakSet<object>();
const pathAnnotations = new WeakMap<
  object,
  Readonly<Record<string, PrivacyAnnotation>>
>();
const pathConflicts = new WeakMap<object, Set<string>>();

export const privacy = {
  nonPii: "nonPii",
  null: "null",
  redact: "redact",
  email: "email",
  phone: "phone",
  person_name: "person_name",
  address_line: "address_line",
  company_name: "company_name",
  external_id: "external_id",
  free_text: "free_text",
  structured: { kind: "structured" },
  jsonShape(schema: JsonPrivacySchema): PrivacyAnnotation {
    return { kind: "jsonShape", schema };
  },
  object<T extends object>(
    definition: T,
    annotation: { kind: "structured" } | "null" = { kind: "structured" },
  ): T {
    const previous = objectAnnotations.get(definition);
    if (
      previous !== undefined &&
      !equalPrivacyAnnotations(previous, annotation)
    ) {
      objectConflicts.add(definition);
    } else {
      objectAnnotations.set(definition, annotation);
    }
    return definition;
  },
  paths<T extends object>(
    schema: T,
    paths: Readonly<Record<string, PrivacyAnnotation>>,
  ): T {
    const previous: Readonly<Record<string, PrivacyAnnotation>> =
      pathAnnotations.get(schema) ?? Object.create(null);
    const combined: Record<string, PrivacyAnnotation> = Object.assign(
      Object.create(null),
      previous,
    );
    for (const [path, annotation] of Object.entries(paths)) {
      const old = Object.hasOwn(previous, path) ? previous[path] : undefined;
      if (old !== undefined && !equalPrivacyAnnotations(old, annotation)) {
        const conflicts = pathConflicts.get(schema) ?? new Set<string>();
        conflicts.add(path);
        pathConflicts.set(schema, conflicts);
      } else {
        combined[path] = annotation;
      }
    }
    pathAnnotations.set(schema, combined);
    return schema;
  },
} as const;

export function hasObjectPrivacyConflict(value: object): boolean {
  return objectConflicts.has(value);
}
export function getObjectPrivacy(value: object): PrivacyAnnotation | undefined {
  return objectAnnotations.get(value);
}
export function getPathPrivacy(
  value: object,
): Readonly<Record<string, PrivacyAnnotation>> {
  return pathAnnotations.get(value) ?? Object.create(null);
}
/** Conflicts are enforced only after the authoritative schema is selected. */
export function getPathPrivacyConflicts(value: object): readonly string[] {
  return [...(pathConflicts.get(value) ?? [])].sort();
}
export function equalPrivacyAnnotations(
  a: PrivacyAnnotation,
  b: PrivacyAnnotation,
): boolean {
  return (
    a === b ||
    (typeof a === "object" &&
      typeof b === "object" &&
      a.kind === b.kind &&
      (a.kind === "structured" ||
        (b.kind === "jsonShape" && a.schema === b.schema)))
  );
}
export function isPrivacyAnnotation(
  value: unknown,
): value is PrivacyAnnotation {
  if (typeof value === "string") {
    return (
      value === "nonPii" ||
      value === "null" ||
      value === "redact" ||
      semanticTypeNames.has(value)
    );
  }
  return (
    typeof value === "object" &&
    value !== null &&
    "kind" in value &&
    (value.kind === "structured" ||
      (value.kind === "jsonShape" &&
        "schema" in value &&
        typeof value.schema === "object" &&
        value.schema !== null &&
        "_zod" in value.schema &&
        typeof value.schema._zod === "object" &&
        value.schema._zod !== null))
  );
}
